import { useState } from "react";
import { Globe, Loader2, MapPin, Smartphone } from "lucide-react";
import { Card, Chip, ProvenanceTag, Sheet } from "@/ui";
import { replays } from "@/mock";
import type { ReplaySession } from "@/mock";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useReplays, useReplayDetail } from "@/data/hooks";
import { PanelHeader } from "../parts";
import { useSignalsQuery, matches } from "../searchContext";
import { ReplayPlayer } from "./ReplayPlayer";
import { RrwebPlayer } from "./RrwebPlayer";
import {
  AigenTag,
  DemoNote,
  EmptyState,
  SampleChip,
  Skeleton,
  durationLabel,
  panelState,
  relativeTime,
} from "../wiring";

/** Classify a raw device string as mobile vs web for the row icon. */
function isMobileDevice(device: string): boolean {
  const d = device.toLowerCase();
  return /iphone|ipad|android|pixel|mobile|ios/.test(d);
}

/**
 * Adapt a real replay-list row into the ReplaySession shape the existing
 * ReplayPlayer expects — this only carries session METADATA (device, duration,
 * counts) for the list row and sheet header.
 *
 * Playback IS wired to the real event stream: ReplayStage fetches
 * /replays/{id} via useReplayDetail and, for web sessions with >=2 events, plays
 * them through the real rrweb Replayer (RrwebPlayer). Mobile sessions and streams
 * too short to replay fall back to the scripted wireframe preview (ReplayPlayer).
 */
function adapt(r: {
  session_id: string;
  started: number;
  last: number;
  duration_seconds: number;
  event_count: number;
  device: string;
  source: string;
}): ReplaySession {
  const mobile = isMobileDevice(r.device);
  return {
    id: r.session_id,
    kind: mobile ? "mobile" : "web",
    device: r.device || "Unknown device",
    os: mobile ? "Mobile" : "Desktop",
    browser: mobile ? undefined : "Browser",
    location: r.source || "—",
    startedAt: relativeTime(r.started),
    durationSec: Math.max(1, Math.round(r.duration_seconds)),
    pages: 0,
    events: r.event_count,
    activity: 0,
    source: "posthog", // ProvenanceTag union; real provenance shown via AigenTag
  };
}

/**
 * Decides how to play one session: the real rrweb-player when there's a web
 * recording (≥2 events), otherwise the scripted wireframe (mobile, sample data,
 * or a stream too short to replay). Hooks live here (not in the list) so they
 * run unconditionally for the open session.
 */
function ReplayStage({
  session,
  siteId,
  isLive,
}: {
  session: ReplaySession;
  siteId: string | undefined;
  isLive: boolean;
}) {
  const [unsupported, setUnsupported] = useState(false);
  const detailQ = useReplayDetail(siteId, session.id, isLive);

  // Sample / offline data has no real recording to fetch.
  if (!isLive) return <ReplayPlayer session={session} />;

  if (detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-card bg-paper-2 py-12 text-[13px] text-muted dark:bg-white/5">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading recording…
      </div>
    );
  }

  const detail = detailQ.data;
  const events = detail?.events ?? [];
  const canReplay = detail?.source === "web" && events.length >= 2 && !unsupported;

  if (canReplay) {
    return (
      <div className="space-y-2">
        <RrwebPlayer events={events} onUnsupported={() => setUnsupported(true)} />
        <p className="text-center font-mono text-[10px] text-subtle">
          web replay · rrweb dom snapshots · {events.length} events
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <ReplayPlayer session={session} />
      <p className="text-center font-mono text-[10px] text-subtle">
        {detail?.source === "mobile"
          ? "mobile replay · rendered as wireframe (posthog mobile → rrweb)"
          : "wireframe preview · no full web recording for this session"}
      </p>
    </div>
  );
}

/** Activity meter — coral only when the session is hot (>=80). */
function Activity({ value }: { value: number }) {
  const hot = value >= 80;
  return (
    <span className="flex items-center gap-1.5">
      <span className="h-1.5 w-14 overflow-hidden rounded-full bg-paper-2 dark:bg-white/5">
        <span
          className={"block h-full rounded-full " + (hot ? "bg-accent" : "bg-ink-strong/50 dark:bg-white/40")}
          style={{ width: `${value}%` }}
        />
      </span>
      <span className="font-mono text-[11px] text-subtle">{value}</span>
    </span>
  );
}

export function Replays() {
  const [active, setActive] = useState<ReplaySession | null>(null);
  const { siteId, live } = useWorkspace();
  const replaysQ = useReplays(siteId);

  const state = panelState({
    siteId,
    workspaceLive: live,
    isLoading: replaysQ.isLoading,
    isError: replaysQ.isError,
    hasData: !!replaysQ.data,
  });

  const isLive = state === "live";
  const seeded = !!replaysQ.data?.seeded;

  const sessions: ReplaySession[] = isLive
    ? (replaysQ.data?.sessions ?? []).map(adapt)
    : replays;

  const q = useSignalsQuery();
  const shown = sessions.filter((r) => matches(q, r.device, r.location, r.os, r.id));
  const empty = isLive && sessions.length === 0;
  const noMatch = !!q && shown.length === 0 && sessions.length > 0;

  return (
    <div className="stagger space-y-6">
      <PanelHeader
        title="Replays"
        desc="Watch real sessions — web and mobile, merged into one list."
        action={
          isLive ? (
            <span className="flex items-center gap-2">
              <AigenTag />
              {seeded && <DemoNote />}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <ProvenanceTag source="posthog" />
              <SampleChip />
            </span>
          )
        }
      />

      {state === "loading" ? (
        <Skeleton className="h-[360px]" />
      ) : empty ? (
        <EmptyState
          title="No replays yet"
          detail="Sessions are recorded once the replay SDK starts capturing on this property."
        />
      ) : noMatch ? (
        <EmptyState title={`No sessions match “${q}”`} detail="Search by device, location or session id." />
      ) : (
        <Card className="p-2 sm:p-3">
          <ul>
            {shown.map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => setActive(r)}
                  className="flex w-full items-center gap-3 rounded-card px-3 py-3 text-left transition-colors hover:bg-paper-2 dark:hover:bg-white/5"
                >
                  <span
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-paper-2 text-muted dark:bg-white/5"
                    aria-hidden
                  >
                    {r.kind === "mobile" ? (
                      <Smartphone className="h-[18px] w-[18px]" strokeWidth={1.5} />
                    ) : (
                      <Globe className="h-[18px] w-[18px]" strokeWidth={1.5} />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-[14px] font-medium text-ink-strong">
                        {r.device}
                      </span>
                      <span className="text-[12px] text-subtle">{r.os}</span>
                      {r.flag && <Chip>{r.flag}</Chip>}
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-muted">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" strokeWidth={1.5} />
                        {r.location}
                      </span>
                      <span className="font-mono text-[11px] text-subtle">{r.startedAt}</span>
                      <span>{durationLabel(r.durationSec)}</span>
                      <span>{r.events} events</span>
                    </span>
                  </span>
                  {!isLive && (
                    <span className="hidden shrink-0 sm:block">
                      <Activity value={r.activity} />
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Sheet
        open={!!active}
        onClose={() => setActive(null)}
        title={active ? `${active.device} · ${active.location}` : ""}
        className="sm:max-w-md"
      >
        {active && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-[12px] text-muted">
              <Chip mono>{active.kind}</Chip>
              <span>{active.os}</span>
              {active.browser && <span>· {active.browser}</span>}
              <span className="font-mono text-[11px] text-subtle">{active.startedAt}</span>
              {isLive ? (
                <AigenTag className="ml-auto" />
              ) : (
                <ProvenanceTag source={active.source} className="ml-auto" />
              )}
            </div>

            {/* Real rrweb playback when the session has a web recording;
                wireframe fallback for mobile / too-short streams (#16). */}
            <ReplayStage session={active} siteId={siteId} isLive={isLive} />

            <div className="grid grid-cols-2 gap-2 border-t border-hairline pt-4">
              <div>
                <p className="eyebrow">Duration</p>
                <p className="mt-1 text-[15px] font-semibold text-ink-strong">
                  {durationLabel(active.durationSec)}
                </p>
              </div>
              <div>
                <p className="eyebrow">Events</p>
                <p className="mt-1 text-[15px] font-semibold text-ink-strong">{active.events}</p>
              </div>
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}
